import { ScreensId } from 'enums/ConfigurableTypes';
import * as Excel from 'exceljs';
import { ValueType } from 'exceljs';
import Papa from 'papaparse';
import { Entity } from 'types/common';
import { dateUtils } from 'utils/date-utils';
import { ExportImportActions } from '../Actions';
import { ImportAdditionalData, ImportColumn } from '../Actions/Types';
import { AutomaticAssetImportNaming } from '../Components/Types';

export class CImportData {
  private _ScreenId!: ScreensId;
  private needReloadTemplateColumns!: boolean;
  private _ImportAssetImageFiles: boolean = false;
  private _AutoGenerateAssetNamesBasedOn: Entity<number> | null = {
    Id: AutomaticAssetImportNaming.NA as number,
    Name: 'N/A',
  };
  private _UpdateQRCode: boolean = false;
  private _ImportAdditionalData: ImportAdditionalData =
    ImportAdditionalData.none;
  public TemplateColumns: Entity<string>[] | undefined;
  public File: File | undefined;
  public Images: File[] | undefined;
  public RowCount: number | undefined;
  public Header: ImportColumn[] | undefined;
  public Table: string[][] | undefined;
  public AddOnly: boolean | undefined = false;
  book: Excel.Workbook;
  constructor() {
    this.book = new Excel.Workbook();
  }
  public get ScreenId() {
    return this._ScreenId;
  }
  public set ScreenId(screenId: ScreensId) {
    if (this._ScreenId !== screenId) {
      this.needReloadTemplateColumns = true;
    }
    this._ScreenId = screenId;
  }
  public get ImportAssetImageFiles() {
    return this._ImportAssetImageFiles;
  }
  public set ImportAssetImageFiles(value: boolean) {
    this._ImportAssetImageFiles = value;
  }
  public get AutoGenerateAssetNamesBasedOn() {
    return this._AutoGenerateAssetNamesBasedOn;
  }
  public set AutoGenerateAssetNamesBasedOn(value: Entity<number> | null) {
    this._AutoGenerateAssetNamesBasedOn = value;
  }
  public get UpdateQRCode() {
    return this._UpdateQRCode;
  }
  public set UpdateQRCode(value: boolean) {
    this._UpdateQRCode = value;
  }
  public get ImportAdditionalData() {
    return this._ImportAdditionalData;
  }
  public set ImportAdditionalData(value: ImportAdditionalData) {
    this._ImportAdditionalData = value;
  }
  public ColumnCount() {
    var count: number = 0;
    if (this.Header !== undefined) {
      this.Header.forEach(c => {
        if (c.Include) {
          count++;
        }
      });
    }
    return count;
  }
  private Clear() {
    this.AddOnly = false;
    this.RowCount = undefined;
    this.Header = undefined;
    this.Table = undefined;
  }
  Init(File: File) {
    this.File = File;
    this.Clear();
  }
  ClearImages() {
    this.Images = undefined;
  }
  AddImages(Files: File[]) {
    if (this.Images === undefined) {
      this.Images = Files;
    } else {
      for (const f of Files) {
        const ff = this.Images.find(v => v.name === f.name);
        if (ff === undefined) {
          this.Images.push(f);
        }
      }
    }
  }
  RemoveImage(name: string) {
    if (this.Images !== undefined) {
      const fi = this.Images.findIndex(v => v.name === name);
      if (fi >= 0) {
        this.Images.splice(fi, 1);
      }
    }
  }
  LoadTemplateColumns(): Promise<any> {
    if (
      this.needReloadTemplateColumns ||
      this.TemplateColumns === undefined ||
      this.TemplateColumns.length === 0
    ) {
      if (this.ScreenId !== undefined) {
        this.needReloadTemplateColumns = false;
        return ExportImportActions.TemplateColumns(
          this.ScreenId,
          this.ImportAdditionalData,
        );
      } else {
        return Promise.resolve();
      }
    } else {
      return Promise.resolve();
    }
  }
  public InitHeaderByTemplate() {
    if (this.TemplateColumns !== undefined && this.TemplateColumns.length > 0) {
      this.Header?.forEach((hvalue, cn) => {
        const v = this.TemplateColumns?.find(tvalue => {
          return tvalue.Name === hvalue.ExcelName;
        });
        if (this.Header !== undefined && v !== undefined) {
          this.Header[cn].Name = v.Id;
        }
      });
    }
  }
  SetTemplateColumns(values: Entity<string>[] | undefined) {
    if (values !== undefined) {
      this.TemplateColumns = values.map(value => {
        return {
          Id: value.Id,
          //Name: value.hasCustomName ? value.CustomName : value.DisplayName,
          Name: value.Name,
        };
      });
      this.InitHeaderByTemplate();
    }
  }
  private GetCellData(cell: Excel.Cell): string {
    let value: any;
    if (cell.value === undefined) {
      return '';
    } else {
      value = cell.value;
    }
    switch (cell.type) {
      case ValueType.Null:
        return '';
      case ValueType.Number:
      case ValueType.String:
      case ValueType.Boolean:
      case ValueType.SharedString:
      case ValueType.Formula:
      case ValueType.RichText:
        return value.toString();
      case ValueType.Date:
        const d = dateUtils.shortDateTimeFormat(value);
        return d !== null ? d : '';
      case ValueType.Hyperlink:
        return value.text;
      case ValueType.Error:
        return '';
      default:
        return '';
    }
  }
  LoadXlsx(): Promise<any> {
    if (this.File !== undefined) {
      var reader: any;
      reader = this.book.xlsx;
      return reader
        .load(this.File)
        .then(() => {
          const ws = this.book.getWorksheet(1);
          this.RowCount = ws.rowCount - 1;
          const row1 = ws.getRow(1);
          this.Header = [];
          row1.eachCell((cell, cn) => {
            this.Header?.push({
              Include: true,
              ExcelName: cell.value?.toString(),
              Name: undefined,
            });
          });
          if (this.Header !== undefined && this.Header.length) {
            this.InitHeaderByTemplate();
            const rc = ws.rowCount < 7 ? ws.rowCount + 1 : 7;
            this.Table = [];
            for (let i = 2; i < rc; i++) {
              const row = ws.getRow(i);
              // init tr
              let tr: string[] = this.Header.map(() => '');
              row.eachCell((cell, cn) => {
                const s = this.GetCellData(cell);
                tr[cn - 1] = s !== undefined ? s : '';
              });
              this.Table?.push(tr);
            }
            return true;
          }
        })
        .catch(err => {
          console.log(err.message);
        });
    } else {
      this.Clear();
      return Promise.resolve();
    }
  }

  LoadCsv(): Promise<any> {
    if (this.File !== undefined) {
      const file = this.File;
      return new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          dynamicTyping: true,
          complete: results => {
            this.RowCount = results.data.length - 1;
            const row1 = results.meta.fields;
            this.Header = [];
            row1?.forEach((cell, cn) => {
              this.Header?.push({
                Include: true,
                ExcelName: cell,
                Name: undefined,
              });
            });
            this.InitHeaderByTemplate();
            const rc = this.RowCount < 6 ? this.RowCount : 5;
            this.Table = [];
            for (let i = 0; i < rc; i++) {
              const obj = results.data[i] as {};
              if (obj !== undefined) {
                const d = Object.keys(obj).map(key => {
                  const v = obj[key];
                  if (typeof v === 'boolean') return v.toString();
                  else return v;
                });
                this.Table?.push(d);
              }
            }
            resolve(true);
          },
          error: error => {
            reject(error);
          },
        });
      });
    } else {
      this.Clear();
      return Promise.resolve();
    }
  }

  LoadExcelfile(): Promise<any> {
    if (this.File !== undefined) {
      const ext = this.File.name.split('.').pop();
      switch (ext) {
        case 'xlsx':
          return this.LoadXlsx();
        case 'csv':
          return this.LoadCsv();
        default:
          this.Clear();
          return Promise.resolve();
      }
    } else {
      this.Clear();
      return Promise.resolve();
    }
  }
}
export const ImprotData = new CImportData();
